import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { QRCodeSVG } from 'qrcode.react';
import { CreditCard, QrCode, Copy, Clock, CheckCircle, XCircle, AlertTriangle, Loader2, Upload } from 'lucide-react';

const statusConfig = {
  aguardando_pagamento: { label: 'Aguardando', color: 'bg-amber-400/10 text-amber-400 border-amber-400/20', icon: Clock },
  pago: { label: 'Pago', color: 'bg-green-400/10 text-green-400 border-green-400/20', icon: CheckCircle },
  atrasado: { label: 'Atrasado', color: 'bg-red-400/10 text-red-400 border-red-400/20', icon: AlertTriangle },
  cancelado: { label: 'Cancelado', color: 'bg-zinc-400/10 text-zinc-400 border-zinc-400/20', icon: XCircle },
};

const fmt = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

export default function AlunoMensalidadesPage() {
  const [mensalidades, setMensalidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pixDialog, setPixDialog] = useState(null);
  const [pixLoading, setPixLoading] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/aluno/mensalidades');
      setMensalidades(res.data);
    } catch { toast.error('Erro ao carregar mensalidades'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const gerarPix = async (mensalidadeId) => {
    setPixLoading(true);
    try {
      const res = await api.post(`/aluno/mensalidades/${mensalidadeId}/gerar-pix`);
      setPixDialog(res.data);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
    setPixLoading(false);
  };

  const copyPix = () => {
    if (pixDialog?.pix_code) {
      navigator.clipboard.writeText(pixDialog.pix_code);
      toast.success('Codigo PIX copiado!');
    }
  };

  const enviarComprovante = async (mensalidadeId, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('comprovante', file);

    setUploadingId(mensalidadeId);

    try {
      await api.post(
        `/aluno/mensalidades/${mensalidadeId}/comprovante`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('Comprovante enviado com sucesso!');
      fetchData();

    } catch (err) {
      toast.error(formatApiError(err));
    }

    setUploadingId(null);
  };


  const proxima = mensalidades.find(m => m.status === 'aguardando_pagamento' || m.status === 'atrasado');

  if (loading) {
    return (
      <div className="space-y-6" data-testid="aluno-mensalidades-loading">
        <Skeleton className="h-8 w-48 bg-[#141414]" />
        <Skeleton className="h-32 bg-[#141414]" />
        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-20 bg-[#141414]" />)}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="aluno-mensalidades-page">
      <h1 className="text-3xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white">MINHAS MENSALIDADES</h1>

      {proxima && (
        <Card className={`border ${proxima.status === 'atrasado' ? 'bg-red-500/5 border-red-500/20' : 'bg-[#007AFF]/5 border-[#007AFF]/20'}`} data-testid="proxima-mensalidade-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-500 mb-1">
                  {proxima.status === 'atrasado' ? 'PAGAMENTO ATRASADO' : 'PROXIMA COBRANCA'}
                </p>
                <p className="text-3xl font-black font-['Barlow_Condensed'] text-white">{fmt(proxima.valor)}</p>
                <p className="text-sm text-zinc-400 mt-1">Competencia: {proxima.competencia} | Vencimento: {proxima.data_vencimento}</p>
              </div>
              <Button
                onClick={() => proxima.pix_code ? setPixDialog({ pix_code: proxima.pix_code, valor: proxima.valor }) : gerarPix(proxima.id)}
                disabled={pixLoading}
                className={proxima.status === 'atrasado' ? 'bg-red-600 hover:bg-red-700' : 'bg-[#007AFF] hover:bg-[#0066DD]'}
                data-testid="pagar-proxima-btn"
              >
                {pixLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <QrCode className="w-4 h-4 mr-2" />}
                {proxima.pix_code ? 'Ver PIX' : 'Pagar com PIX'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {mensalidades.length === 0 ? (
          <Card className="bg-[#141414] border-[#27272A]">
            <CardContent className="p-8 text-center">
              <CreditCard className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-500">Nenhuma mensalidade encontrada.</p>
            </CardContent>
          </Card>
        ) : mensalidades.map(m => {
          const cfg = statusConfig[m.status] || statusConfig.aguardando_pagamento;
          const StatusIcon = cfg.icon;
          return (
            <Card key={m.id} className="bg-[#141414] border-[#27272A] hover:border-[#3f3f46] transition-all" data-testid={`aluno-mensalidade-${m.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${cfg.color.split(' ')[0]}`}>
                      <StatusIcon className={`w-4 h-4 ${cfg.color.split(' ')[1]}`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">Competencia {m.competencia}</p>
                      <p className="text-xs text-zinc-500">Vencimento: {m.data_vencimento} {m.data_pagamento ? `| Pago em: ${m.data_pagamento.split('T')[0]}` : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="text-lg font-bold font-['Barlow_Condensed'] text-white">{fmt(m.valor)}</p>
                    <Badge className={cfg.color}>{cfg.label}</Badge>
                    {(m.status === 'aguardando_pagamento' || m.status === 'atrasado' || m.status === 'comprovante_enviado') && (
  <div className="flex items-center gap-2">

    <Button
      size="sm"
      variant="ghost"
      onClick={() => m.pix_code ? setPixDialog({ pix_code: m.pix_code, valor: m.valor }) : gerarPix(m.id)}
      disabled={pixLoading}
      className="text-[#007AFF] hover:text-blue-300"
      data-testid={`pix-btn-${m.id}`}
    >
      <QrCode className="w-4 h-4" />
    </Button>

    <label className="cursor-pointer">
      <input
        type="file"
        className="hidden"
        accept=".jpg,.jpeg,.png,.webp,.pdf"
        onChange={(e) => enviarComprovante(m.id, e.target.files[0])}
      />

      <div className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-zinc-800 text-green-400">
        {uploadingId === m.id ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Upload className="w-4 h-4" />
        )}
      </div>
    </label>

  </div>
)}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!pixDialog} onOpenChange={() => setPixDialog(null)}>
        <DialogContent className="bg-[#141414] border-[#27272A] sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase flex items-center gap-2">
              <QrCode className="w-5 h-5 text-[#007AFF]" /> Pagar com PIX
            </DialogTitle>
          </DialogHeader>
          {pixDialog && (
            <div className="space-y-4 text-center" data-testid="pix-dialog-content">
              <p className="text-2xl font-black font-['Barlow_Condensed'] text-white">{fmt(pixDialog.valor)}</p>
              <div className="bg-white p-4 rounded-lg inline-block mx-auto">
                <QRCodeSVG value={pixDialog.pix_code} size={200} />
              </div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Copia e Cola</p>
                <div className="flex gap-2">
                  <Input value={pixDialog.pix_code} readOnly className="bg-[#0A0A0A] border-[#27272A] text-xs" data-testid="aluno-pix-code-input" />
                  <Button size="icon" variant="outline" onClick={copyPix} className="border-[#27272A] shrink-0" data-testid="aluno-copy-pix-btn">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <p className="text-xs text-zinc-500">Aguardando confirmacao manual do pagamento pela escola.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
